<script lang="ts" module>
  export type SystemMenuItem = {
    datacenter: string;
    systems: {
      name: string;
      maintainerId: string;
      type: 'hp' | 'emc' | 'netapp';
    }[];
  };
</script>

<script lang="ts">
  import { submenu, menus, systemSubmenus, type MenuEntry } from './Sidebar.Definitions';
  import {
    faBug,
    faCogs,
    faDiagramProject,
    faGears,
    faSearch,
  } from '@fortawesome/free-solid-svg-icons';
  import SidebarEntry from './SidebarEntry.svelte';
  import Fuse from 'fuse.js';
  import Fa from 'svelte-fa';
  import { dev } from '$app/env';
  import { page } from '$app/state';
  import { untrack } from 'svelte';
  import { SvelteMap } from 'svelte/reactivity';

  interface Props {
    feMode: string;
    showHpVmware?: boolean;
    datacenters: SystemMenuItem[];
  }

  let { feMode, showHpVmware, datacenters }: Props = $props();

  let allDcEntries = $derived(
    datacenters.map(({ datacenter, systems }) => ({
      title: datacenter,
      icon: faCogs,
      content: submenu(
        ...systems.map(({ name, maintainerId, type }) => ({
          title: name,
          content: submenu(...systemSubmenus[type](maintainerId)),
        })),
      ),
    })) as MenuEntry[],
  );

  const f = new Fuse<MenuEntry>([], {
    keys: ['title'],
    threshold: 0.2,
  });
  function findDatacenters(allDatacenters: MenuEntry[], search: string): MenuEntry[] {
    if (search.length === 0) return allDatacenters;

    return allDatacenters
      .flatMap((dc) => {
        if (!('children' in dc.content)) return [];

        f.setCollection(dc.content.children);

        return f.search(search).map(({ item }) => item);
      })
      .filter((dc) => Boolean(dc)) as MenuEntry[];
  }

  let search = $state('');
  let dc = $derived(findDatacenters(allDcEntries, search.trim()));
  let entries = $derived(menus[feMode] ?? []);
  let openState = new SvelteMap<symbol, boolean>();

  let dragMode = $state(false);
  function onDrag(e: MouseEvent) {
    if (dragMode) {
      const width = Math.max(220, e.clientX + 2);
      document.body.style.setProperty('--sidebar-width', `${width}px`);
    }
  }

  function detectOpenState(path: string) {
    function openIfContains(entries: MenuEntry[]): boolean {
      let hasOpen = false;

      for (const entry of entries) {
        if ('href' in entry.content) {
          if (entry.content.href === path) hasOpen = true;
        } else if ('children' in entry.content) {
          const next = openIfContains(entry.content.children);

          openState.set(entry.content.key, next);

          if (next) hasOpen = true;
        }
      }

      return hasOpen;
    }

    openIfContains(entries);
    openIfContains(allDcEntries);
  }

  $effect(() => {
    datacenters;
    page.url.pathname;

    untrack(() => {
      detectOpenState(page.url.pathname);
    });
  });
</script>

<div
  class="relative flex flex-col overflow-x-hidden overflow-y-auto bg-slate-700 pr-0.5 pb-2 text-xs text-white dark:bg-slate-900"
>
  <menu>
    {#each entries as entry}
      <SidebarEntry {entry} {openState} {showHpVmware} />
    {/each}
    {#if dev}
      <SidebarEntry
        entry={{
          title: 'Artificial Data',
          icon: faBug,
          content: {
            href: '/artificial',
          },
        }}
        {openState}
      />
    {/if}
    <SidebarEntry
      entry={{
        title: 'Storage Configuration',
        icon: faGears,
        content: {
          href: '/storage-config',
        },
      }}
      {openState}
    />
    <SidebarEntry
      entry={{
        title: 'Maintainers',
        icon: faDiagramProject,
        content: {
          href: '/maintainers',
        },
      }}
      {openState}
    />
  </menu>
  <h2 class="bg-black/25 p-2 text-gray-400 uppercase">Storage Arrays</h2>
  <label
    class="m-2 flex items-center gap-2 border-b-2 border-gray-500 p-2 text-gray-500 transition-colors focus-within:border-gray-300 focus-within:text-white"
  >
    <Fa icon={faSearch} />
    <input
      type="text"
      class="min-w-0 border-none bg-transparent p-0 text-sm text-white placeholder:text-gray-500 focus:ring-0"
      placeholder="Search..."
      bind:value={search}
    />
  </label>
  <menu>
    {#each dc as entry}
      <SidebarEntry {entry} {openState} />
    {/each}
  </menu>

  <!-- svelte-ignore a11y_no_static_element_interactions -->
  <div
    class="absolute inset-0 left-auto w-0.5 cursor-col-resize transition-colors hover:bg-gray-400"
    draggable="true"
    ondragstart={(e) => {
      e.preventDefault();
      dragMode = true;
    }}
  ></div>

  <div class="mt-auto p-1 text-center text-gray-500">
    {__APP_VERSION__}
  </div>
</div>

<svelte:window onmousemove={onDrag} onmouseup={() => (dragMode = false)} />
